import React, { ReactElement } from 'react';
import styled, { useTheme } from 'styled-components';
import { mdiCheckboxMarkedCircle, mdiRadioboxBlank, mdiCloseCircle } from '@mdi/js';
import ReactIcon from '@mdi/react';

import { ContentQuizQuestionsState, ContentQuizQuestionState } from 'features/contents';
import { Box, Button, ButtonIcon, Flex, Hr, Label, Radio, RichContent, Typography } from 'components';
import { createMarkup } from 'app/utils';
import Player from 'features/player';

interface QuizSidebarProps {
  questions: ContentQuizQuestionsState;
  updateQuestions: (question: ContentQuizQuestionState) => void;
  currentQuestion: string;
  setCurrentQuestion: React.Dispatch<React.SetStateAction<string>>;
  questionsCompleted: boolean;
  sendQuestions: () => void;
  questionsSended: boolean;
}

const Icon = styled(ReactIcon)`
  width: 24px;
  color: ${(props: any) => props.theme.colors[props['data-color']]};
`;

const QuestioNumber = styled(Typography)`
  position: relative;

  &::before {
    content: '';
    display: block;
    background: ${({ theme }) => theme.colors.primary_400};
    position: absolute;

    ${({ theme: { mediaQueries } }) => {
      return `
        width: 5px;
        height: 5px;
        top: 11px;
        left: calc(100% + 3px);

        ${mediaQueries.desktop} {
          width: 8px;
          height: 8px;
          top: 20px;
          left: calc(100% + 3px);
        }
      `;
    }}
  }
`;

const QuizQuestion = ({
  questions,
  currentQuestion,
  updateQuestions,
  setCurrentQuestion,
  questionsCompleted,
  sendQuestions,
  questionsSended,
}: QuizSidebarProps): ReactElement => {
  const question = questions[currentQuestion];
  const { currentTheme } = useTheme();

  if (!question) return <></>;

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateQuestions({ ...question, answerUuid: e.target.value });
  };

  const letters = ['a', 'b', 'c', 'd', 'e', 'f', 'g'];

  return (
    <Flex flexDirection="column">
      <Flex flexGrow={0} pt={{ m: 40, d: 60 }} px={{ m: 40, d: 60 }} flexWrap="nowrap">
        <Flex width={{ m: 40, d: 70 }} flexGrow={0} flexShrink={0} position="relative">
          <QuestioNumber type="type6" fontWeight="bold" color="color100">
            {`${question.questionIndex}`.padStart(2, '0')}
          </QuestioNumber>
        </Flex>

        <Flex pt={{ d: '10px' }}>
          <Typography type="type3" color="color100" lineHeight="1.5">
            <div dangerouslySetInnerHTML={createMarkup(question.questionText)} />
          </Typography>
        </Flex>
      </Flex>

      <Flex flexGrow={0} flexDirection="column" mt={4}>
        {question.answerChoices.map((answer, index) => {
          let answerColor = 'color100';
          let answerPath = mdiCheckboxMarkedCircle;
          let answerFontWeight = 'normal';

          if (
            question.rightQuestionAnswerChoiceUuid &&
            question.rightQuestionAnswerChoiceUuid === answer.answerUuid &&
            question.answerUuid === answer.answerUuid
          ) {
            answerColor = 'primary_400';
            answerPath = mdiCheckboxMarkedCircle;
            answerFontWeight = 'bold';
          }

          if (
            question.rightQuestionAnswerChoiceUuid &&
            question.rightQuestionAnswerChoiceUuid === answer.answerUuid &&
            question.answerUuid !== answer.answerUuid
          ) {
            answerColor = 'primary_400';
            answerPath = mdiCheckboxMarkedCircle;
            answerFontWeight = 'bold';
          }

          if (
            question.rightQuestionAnswerChoiceUuid &&
            question.rightQuestionAnswerChoiceUuid !== answer.answerUuid &&
            question.answerUuid === answer.answerUuid
          ) {
            answerColor = 'alert_400';
            answerPath = mdiCloseCircle;
            answerFontWeight = 'bold';
          }

          if (
            question.rightQuestionAnswerChoiceUuid &&
            question.rightQuestionAnswerChoiceUuid !== answer.answerUuid &&
            question.answerUuid !== answer.answerUuid
          ) {
            answerColor = 'color50';
            answerPath = mdiRadioboxBlank;
          }

          return (
            <Flex
              key={`answer-${answer.answerUuid}`}
              bg={question.answerUuid === answer.answerUuid ? 'quizActiveQuestionBg' : ''}
              flexGrow={0}
              py={2}
              my={1}
            >
              <Flex flexGrow={0} pl={{ m: 80, d: 130 }} pr={{ m: 40, d: 60 }} flexWrap="nowrap">
                <Flex flexGrow={0} key={answer.answerUuid} flexWrap="nowrap">
                  <Box flexGrow={0} mr={{ m: '10px' }} pt="3px">
                    {Boolean(question.rightQuestionAnswerChoiceUuid) ? (
                      <>
                        <Icon data-color={answerColor} path={answerPath} />
                      </>
                    ) : (
                      <Radio
                        name="answerChoice"
                        checked={question.answerUuid === answer.answerUuid}
                        value={answer.answerUuid}
                        onChange={handleOnChange}
                        id={`answer-${answer.answerUuid}`}
                      />
                    )}
                  </Box>

                  <Label htmlFor={`answer-${answer.answerUuid}`}>
                    <Flex flexWrap="nowrap" flexGrow={1}>
                      <Box mr={2}>
                        <Typography type="type3" color={answerColor} fontWeight={answerFontWeight} lineHeight="1.5">
                          {letters[index]})
                        </Typography>
                      </Box>

                      <Typography type="type3" color={answerColor} fontWeight={answerFontWeight} lineHeight="1.5">
                        <div dangerouslySetInnerHTML={createMarkup(answer.answerText)} />
                      </Typography>
                    </Flex>
                  </Label>
                </Flex>
              </Flex>
            </Flex>
          );
        })}
      </Flex>

      {Boolean(question.rightQuestionAnswerChoiceUuid && question.commentedAnswer) && (
        <Flex flexGrow={0} pl={{ m: 80, d: 70 }} pr={{ m: 40, d: 60 }} mt={{ d: 30 }} flexDirection="column">
          <Flex flexGrow={0} alignItems="center">
            <Icon
              data-color={question.rightQuestionAnswerChoiceUuid === question.answerUuid ? 'primary_400' : 'alert_400'}
              path={question.rightQuestionAnswerChoiceUuid === question.answerUuid ? mdiCheckboxMarkedCircle : mdiCloseCircle}
            />
            <Typography
              type="type3"
              upper
              fontWeight="bold"
              color={question.rightQuestionAnswerChoiceUuid === question.answerUuid ? 'primary_400' : 'alert_400'}
              ml={1}
            >
              {question.rightQuestionAnswerChoiceUuid === question.answerUuid ? 'Resposta certa' : 'Resposta errada'}
            </Typography>
          </Flex>

          <RichContent defaultType="type3" currentTheme={currentTheme}>
            {question.commentedAnswer}
          </RichContent>

          {Boolean(question && question.externalVideoProviderIdOrUrl) && (
            <Flex mt={{ m: 30 }} flexGrow={0} justifyContent="center">
              <Player
                videoProvider={question && question.videoProvider ? question.videoProvider : 1}
                externalVideoProviderIdOrUrl={question && question.externalVideoProviderIdOrUrl ? question.externalVideoProviderIdOrUrl : ''}
                protectedVideoOtp={question && question.protectedVideoOtp ? question.protectedVideoOtp : ''}
                protectedVideoPlaybackInfo={question && question.protectedVideoPlaybackInfo ? question.protectedVideoPlaybackInfo : ''}
                protectedVideoPlayerTheme={question && question.protectedVideoPlayerTheme ? question.protectedVideoPlayerTheme : ''}
                width={{ m: 320, t: 640, d: '100%' }}
                height={{ m: 180, t: 360, d: 576 }}
                maxWidth={{ d: 1024 }}
                maxHeight={{ d: 576 }}
              />
            </Flex>
          )}
        </Flex>
      )}

      <Hr color="quizActiveQuestionBg" my={5} />

      <Flex flexGrow={0} justifyContent="center" pb={{ d: 60 }}>
        {Boolean(question.prevQuestionUuid) && (
          <ButtonIcon
            icon="arrowLeft"
            onClick={() => question.prevQuestionUuid && setCurrentQuestion(question.prevQuestionUuid)}
            width={40}
            height={40}
            borderRadius="50%"
            title="Pergunta anterior"
          />
        )}

        {Boolean(question.nextQuestionUuid) && (
          <Button ml={2} onClick={() => question.nextQuestionUuid && setCurrentQuestion(question.nextQuestionUuid)} title="Próxima pergunta">
            Próxima pergunta
          </Button>
        )}

        {question.isLast && !questionsSended && (
          <Button
            disabled={!questionsCompleted}
            bg={questionsCompleted ? 'primary_400' : 'gray_500'}
            ml={2}
            title="Enviar atividade"
            onClick={sendQuestions}
          >
            Enviar atividade
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

export default QuizQuestion;
